import type { ComponentType, ReactElement } from "react";
import { Navigate, createBrowserRouter, type RouteObject } from "react-router-dom";
import type { CmtsHostRuntime } from "../app/appRuntime.js";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary.js";
import { CmtsAccessDeniedPage, CmtsLazyRouteBoundary, CmtsNotFoundPage, CmtsProtectedRoute, CmtsProtectedRouteShell, CmtsRouteLoadingFallback, CmtsRuntimeRouteRoot } from "./components/routing/index.js";
import { cmtsRouteDefinitions, type CmtsDataRouteDefinition } from "./config/route.config.js";

function CmtsPublicRouteElement(props: { readonly route: CmtsDataRouteDefinition; readonly runtime: CmtsHostRuntime; readonly Component: ComponentType<{ runtime: CmtsHostRuntime }> }): ReactElement {
  const Page = props.Component;
  return <CmtsLazyRouteBoundary label={props.route.title} routePath={props.route.path}><Page runtime={props.runtime} /></CmtsLazyRouteBoundary>;
}

function CmtsProtectedRouteElement(props: { readonly route: CmtsDataRouteDefinition; readonly runtime: CmtsHostRuntime; readonly Component: ComponentType<{ runtime: CmtsHostRuntime }> }): ReactElement {
  const Page = props.Component;
  return (
    <CmtsProtectedRoute access={props.route.access} routePath={props.route.path}>
      <CmtsProtectedRouteShell runtime={props.runtime}>
        <CmtsLazyRouteBoundary label={props.route.title} routePath={props.route.path}><Page runtime={props.runtime} /></CmtsLazyRouteBoundary>
      </CmtsProtectedRouteShell>
    </CmtsProtectedRoute>
  );
}

function createRouteObject(route: CmtsDataRouteDefinition, runtime: CmtsHostRuntime): RouteObject {
  return {
    id: route.id,
    path: route.path,
    errorElement: <RouteErrorBoundary />,
    hydrateFallbackElement: <CmtsRouteLoadingFallback label={route.title} />,
    loader: () => ({ routeId: route.id, path: route.path, domain: route.domain, requiredPermission: route.access.requiredPermission ?? null }),
    lazy: async () => {
      const { Component } = await route.lazy();
      const DataRouteComponent = route.access.public
        ? function CmtsGeneratedPublicRoute(): ReactElement {
          return <CmtsPublicRouteElement route={route} runtime={runtime} Component={Component} />;
        }
        : function CmtsGeneratedProtectedRoute(): ReactElement {
          return <CmtsProtectedRouteElement route={route} runtime={runtime} Component={Component} />;
        };
      return { Component: DataRouteComponent };
    },
  } satisfies RouteObject;
}

export function createCmtsRuntimeRouter(runtime: CmtsHostRuntime) {
  const dataRoutes = cmtsRouteDefinitions.map((route) => createRouteObject(route, runtime));
  const routeObjects: RouteObject[] = [
    {
      id: "cmts.root",
      path: "/",
      element: <CmtsRuntimeRouteRoot runtime={runtime} />,
      errorElement: <RouteErrorBoundary />,
      children: [
        { index: true, element: <Navigate to="/cmts/my-workspace" replace /> },
        { path: "/auth/login", element: <Navigate to="/cmts/login" replace /> },
        { path: "/dashboard", element: <Navigate to="/cmts/my-workspace" replace /> },
        ...dataRoutes,
        {
          id: "cmts.access-denied.alias",
          path: "/access-denied",
          element: <CmtsAccessDeniedPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          id: "cmts.access-denied",
          path: "/cmts/access-denied",
          element: <CmtsAccessDeniedPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          id: "cmts.not-found.canonical",
          path: "/not-found",
          element: <CmtsNotFoundPage />,
          errorElement: <RouteErrorBoundary />,
        },
        {
          id: "cmts.not-found",
          path: "*",
          element: <CmtsNotFoundPage />,
          errorElement: <RouteErrorBoundary />,
        },
      ],
    },
  ];
  return createBrowserRouter(routeObjects);
}

export { createCmtsHostRoutes } from "../app/appRouter.js";
export * from "./config/route.config.js";

export { BrowserCmtsEnterpriseApp as CmtsModuleRouter } from "../app/BrowserCmtsEnterpriseApp.js";
